<template>
  <v-row class="ma-0" align="stretch">
    <v-col
      v-for="plan in plans"
      :key="plan.id"
      cols="12" sm="6" lg="4"
      class="pa-2"
    >
      <v-card
        class="plan-card rounded-lg d-flex flex-column fill-height"
        :class="{ 'plan-card--active': plan.id === activePlanName }"
        elevation="0"
        outlined
      >
        <v-card-text class="pb-2">

          <!-- Edit Mode -->
          <template v-if="editingName === plan.id">
            <div class="d-flex flex-column" style="gap: 4px;">
              <label class="mono-label">Plan Name</label>
              <v-text-field
                v-model="editName"
                placeholder="Plan name..."
                counter="40"
                :rules="n => n.length <= 40 || 'Max 40 characters'"
                solo-inverted
                background-color="border"
                flat
              />
              <div class="d-flex justify-end mt-1" style="gap: 6px;">
                <v-btn x-small depressed @click="handleResetEdit(plan)">Cancel</v-btn>
                <v-btn x-small depressed color="accent" @click="handleSaveEdit(plan.id)" :disabled="editName.length > 40 || editName === plan.name">Save</v-btn>
              </div>
            </div>
          </template>

          <template v-else>
            <v-row class="ma-0 mb-3" align="start">
              <div class="d-flex flex-column" style="flex: 1; min-width: 0;">
                <div class="d-flex align-center" style="gap: 6px;">
                  <h3 class="plan-name text--text text-truncate">{{ plan.name }}</h3>
                  <v-icon v-if="plan.id === activePlanName" size="14" color="accent">mdi-circle</v-icon>
                </div>
                <span class="text-caption text--secondary mt-1">{{ plan.totalCourses }} courses planned</span>
              </div>
              <v-menu transition="slide-y-transition" offset-y bottom rounded close-on-click nudge-bottom="10">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon v-bind="attrs" v-on="on" small class="mt-1">mdi-dots-horizontal</v-icon>
                </template>
                <v-list dense>
                  <v-list-item dense @click="handleStartEdit(plan)">
                    <v-icon color="accent" class="mr-3" small>mdi-pencil</v-icon>Edit name
                  </v-list-item>
                  <v-list-item dense @click="$emit('delete-plan', plan.id)">
                    <v-icon color="error" class="mr-3" small>mdi-delete</v-icon>Delete plan
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-row>

            <!-- Yearly Distribution bar -->
            <div class="mb-2">
              <div class="d-flex align-center justify-space-between mb-1">
                <span class="mono-label">Distribution</span>
                <span class="year-summary text--secondary font-weight-medium">{{ yearSummary(plan.years) }}</span>
              </div>
              <div class="year-bar">
                <div
                  v-for="(count, yr, idx) in plan.years"
                  :key="yr"
                  class="year-bar-segment"
                  :style="{
                    background: colours[idx],
                    width: plan.totalCourses > 0 ? ((count / plan.totalCourses) * 100) + '%' : '0%'
                  }"
                  :title="'Y' + yr + ': ' + count + ' courses'"
                ></div>
              </div>
            </div>

            <!-- Subject Breakdown -->
            <div class="mb-2">
              <span class="mono-label">Subjects</span>
              <div class="subject-grid mt-1">
                <v-chip
                  v-for="(count, subject) in plan.subjectBreakdown"
                  :key="subject"
                  x-small color="accent" class="font-weight-medium"
                >{{ subject }}: {{ count }}</v-chip>
              </div>
            </div>
          </template>
        </v-card-text>

        <v-spacer />
        <v-divider />

        <v-card-actions class="px-4 py-2">
          <v-icon color="secondary" size="13" class="mr-1">mdi-clock-outline</v-icon>
          <span class="text-caption text--secondary">{{ new Date(plan.updatedAt).toDateString() }}</span>
          <v-spacer />
          <v-btn
            v-if="plan.id !== activePlanName"
            color="accent" text x-small class="px-2"
            @click="$emit('select-plan', plan.id)"
          >
            Activate
          </v-btn>
          <span v-else class="active-label accent--text">
            <v-icon size="11" color="accent" class="mr-1">mdi-check-circle</v-icon>Active
          </span>
        </v-card-actions>
      </v-card>
    </v-col>

    <!-- Create New Plan Card -->
    <v-col v-if="plans.length < 6" cols="12" sm="6" lg="4" class="pa-2">
      <div
        class="create-card d-flex flex-column align-center justify-center text-center fill-height"
        @click="$emit('create-plan', 'blank')"
      >
        <v-icon size="36" color="accent">mdi-plus-circle-outline</v-icon>
        <span class="text-body-2 font-weight-bold mt-2">New plan</span>
        <span class="text-caption text--secondary mt-1" style="max-width: 200px;">
          Map out a new course sequence
        </span>
      </div>
    </v-col>
  </v-row>
</template>

<script>

export default {
  name: 'PlanSwitcher',

  props: {
    plans: {
      type: Array,
      required: true
    },
    activePlanName: {
      type: String,
      default: null
    }
  },

  data: function () {
    return {
      editingName: null,
      editName: '',
      colours: [
        '#003C85',
        '#50ADFF',
        '#FB8C00',
        '#B131F5',
        '#31F5BD',
        '#F53131'
      ]
    }
  },

  methods: {
    handleStartEdit: function (plan) {
      this.editingName = plan.id
      this.editName = plan.name
    },
    handleSaveEdit: function (planId) {
      if (!this.editName.trim()) return
      this.$emit('update-plan-meta', planId, this.editName)
      this.editingName = null
    },
    handleResetEdit: function (plan) {
      this.editingName = null
      this.editName = plan.name
    },
    yearSummary: function (years) {
      return Object.keys(years)
        .map(function (yr) { return 'Y' + yr + ': ' + years[yr] })
        .join(' - ')
    }
  }
}
</script>

<style scoped>
/* Active card highlight */
.plan-card { transition: border-color 0.15s; }
.plan-card--active { border-color: var(--v-accent-base) !important; }

/* Plan name */
.plan-name {
  font-size: 15px;
  font-weight: 700;
  line-height: 1.3;
}

/* Section labels */
.mono-label {
  font-size: 9px;
  font-weight: 600;
  color: var(--v-secondary-base);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

/* Year summary inline text */
.year-summary {
  font-size: 10px;
}

/* Distribution bar */
.year-bar {
  display: flex;
  gap: 2px;
  height: 8px;
  border-radius: 999px;
  overflow: hidden;
  background: var(--v-border-base);
}
.year-bar-segment {
  height: 100%;
  transition: width 0.3s;
}

/* Subject chips */
.subject-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

/* Active label in footer */
.active-label {
  font-size: 11px;
  font-weight: 600;
  display: flex;
  align-items: center;
}

/* Edit input */
.field-input {
  width: 100%;
  font-size: 12px;
  font-weight: 500;
  color: var(--v-text-base);
  background: var(--v-border-base);
  border: 1px solid var(--v-secondary-base);
  border-radius: 6px;
  padding: 6px 10px;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s;
}
.field-input:focus { border-color: var(--v-accent-base); }

/* Create card */
.create-card {
  border: 1px dashed var(--v-secondary-base);
  border-radius: 12px;
  padding: 24px;
  cursor: pointer;
  transition: border-color 0.2s;
}
.create-card:hover {
  border-color: var(--v-accent-base);
}
</style>
